import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Calendar,
  MessageSquare,
  Phone,
  Loader2,
  Clock,
  CheckCircle2,
  User,
  History,
  Users,
} from "lucide-react";
import Select from "react-select";
import { format, isValid, parseISO } from "date-fns";
import { getSimpleCustomers } from "../../../api/crm/customers/customerInfo";
import {
  createContactHistory,
  progressUpdateContactHistory,
} from "../../../api/crm/contact-history/info";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import {
  customerInModalSelectStyles,
  filterCustomerOption,
  formatCustomerOptionLabel,
  mapBranchCustomersToOptions,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";

function formatDateShort(dateStr) {
  if (!dateStr) return "—";
  const raw = String(dateStr).trim();
  const iso = parseISO(raw.includes("T") ? raw : `${raw}T00:00:00`);
  const d = isValid(iso) ? iso : new Date(raw);
  if (!isValid(d)) return raw;
  return format(d, "dd/MM/yyyy");
}

function readSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function readUserBranchId(user = readSessionUser()) {
  return (
    user?.branch?.id ||
    user?.branch_id ||
    user?.branchId ||
    user?.owner_branch_id ||
    user?.branch?.branch_id ||
    ""
  );
}

const ContactHistoryCreateModal = ({
  isOpen,
  onClose,
  editingContact,
  onRefresh,
  mode = "create",
}) => {
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  const methodOptions = [
    { value: "Gọi điện", label: "Gọi điện" },
    { value: "Email", label: "Email" },
    { value: "Gặp trực tiếp", label: "Gặp trực tiếp" },
    { value: "Zalo", label: "Zalo" },
  ];
  const resultQuickOptions = ["Hẹn lại", "Đạt", "Không phản hồi"];

  const currentUser = useMemo(() => readSessionUser(), [isOpen]);
  const fixedBranchId = useMemo(() => readUserBranchId(currentUser), [currentUser]);
  const isProgressMode = mode === "progress";

  useEffect(() => {
    if (!isOpen) return;

    if (!editingContact) {
      setFormData({
        branch_id: fixedBranchId,
        customer_id: "",
        contact_date: new Date().toISOString().split("T")[0],
        method: "",
        content: "",
        result: "",
        next_contact_date: "",
      });
    } else if (isProgressMode) {
      setFormData({
        branch_id: fixedBranchId || editingContact.branch_id || "",
        customer_id: editingContact.customer_id || "",
        contact_date: new Date().toISOString().split("T")[0],
        method: "",
        content: "",
        result: "",
        next_contact_date: "",
      });
    } else {
      setFormData({
        ...editingContact,
        branch_id: fixedBranchId || editingContact.branch_id || "",
        contact_date: editingContact.contact_date?.split("T")[0] || "",
        next_contact_date: editingContact.next_contact_date?.split("T")[0] || "",
      });
    }
  }, [isOpen, editingContact, isProgressMode, fixedBranchId]);

  useEffect(() => {
    if (!isOpen || isProgressMode) return;
    let cancelled = false;
    setCustomersLoading(true);
    getSimpleCustomers({ record_status: "active" })
      .then((res) => {
        if (cancelled) return;
        const items = (res?.items || []).filter(
          (c) => String(c.record_status || "active").toLowerCase() === "active"
        );
        // Giữ KH đang chọn khi sửa (phòng trường hợp đã đổi trạng thái)
        if (
          editingContact?.customer_id &&
          !items.some((c) => String(c.id) === String(editingContact.customer_id))
        ) {
          items.push({
            id: editingContact.customer_id,
            name: editingContact.customer_name || "Khách hàng",
            ward: editingContact.customer_ward || editingContact.ward || "",
            branch_id: editingContact.branch_id || "",
            branch_name: editingContact.branch_name || "",
            record_status: "active",
          });
        }
        setCustomers(items);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setCustomers([]);
          showErrorToast("Không tải được danh sách khách hàng");
        }
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, isProgressMode, editingContact]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = async () => {
    if (loading) return;
    const confirm = await showConfirmDialog({
      title: "Xác nhận huỷ",
      text: "Bạn có chắc muốn huỷ thao tác này?",
      confirmText: "Đồng ý",
      cancelText: "Giữ lại",
    });
    if (confirm.isConfirmed) onClose();
  };

  const handleSave = async () => {
    const { customer_id, contact_date, method, content, result } = formData;
    const missing = [];
    if (!customer_id) missing.push("khách hàng");
    if (!contact_date) missing.push("ngày liên hệ");
    if (!String(method || "").trim()) missing.push("hình thức");
    if (!String(content || "").trim()) missing.push("nội dung trao đổi");
    if (!String(result || "").trim()) missing.push("kết quả");
    if (missing.length) {
      showErrorToast(
        missing.length === 1
          ? `Vui lòng nhập ${missing[0]}`
          : `Vui lòng nhập đầy đủ: ${missing.join(", ")}`
      );
      return;
    }

    const payload = {
      ...formData,
      branch_id: fixedBranchId || formData.branch_id || "",
      staff_id: currentUser?.id,
      result: String(result).trim(),
      content: String(content).trim(),
      method: String(method).trim(),
    };

    try {
      setLoading(true);
      if (mode === "progress" && editingContact?.id) {
        await progressUpdateContactHistory(editingContact.id, payload);
        showSuccessToast("Cập nhật tiến độ thành công");
      } else {
        await createContactHistory(payload);
        showSuccessToast("Thêm lịch sử liên hệ thành công");
      }
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Lỗi khi lưu liên hệ:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Không thể lưu thông tin liên hệ";
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const customerOptions = useMemo(
    () => mapBranchCustomersToOptions(customers),
    [customers]
  );

  const modalSelectStyles = {
    ...customSelectStyles,
    control: (base, state) => ({
      ...customSelectStyles.control(base, state),
      minHeight: 44,
      borderRadius: "0.9rem",
      padding: "0.1rem 0.2rem",
      border: state.isFocused ? "1px solid #b91c1c" : "1px solid #e5e7eb",
      backgroundColor: "#f9fafb",
      boxShadow: "none",
    }),
    valueContainer: (base) => ({ ...base, padding: "0 0.5rem" }),
    menuPortal: (base) => ({ ...base, zIndex: 230 }),
  };

  const customerSelectStyles = {
    ...customerInModalSelectStyles,
    control: modalSelectStyles.control,
    valueContainer: modalSelectStyles.valueContainer,
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Phone className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      {mode === "progress" ? "Cập nhật" : "Ghi nhận"}{" "}
                      <span className="text-red-700">liên hệ</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Nhật ký tương tác khách hàng
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Form Nội dung */}
              <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 py-6 sm:space-y-7 sm:px-6 sm:py-7">
                {/* Progress: tóm tắt liên hệ trước — thay block chọn KH/CN */}
                {isProgressMode && editingContact ? (
                  <div className="space-y-3">
                    <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                      <History className="h-3 w-3" /> Liên hệ trước đó
                    </h3>
                    <div className="rounded-2xl border border-amber-100 bg-amber-50/60 p-4 sm:p-5">
                      <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-red-700 shadow-sm border border-red-100">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-black text-gray-900">
                            {editingContact.customer_name || "Khách hàng"}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] text-gray-500">
                            {[
                              editingContact.branch_name,
                              editingContact.staff_name
                                ? `NV: ${editingContact.staff_name}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" • ") || "—"}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3.5 flex flex-wrap gap-1.5">
                        {editingContact.method ? (
                          <span className="rounded-full border border-purple-100 bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-800">
                            {editingContact.method}
                          </span>
                        ) : null}
                        <span className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-bold text-gray-600">
                          {formatDateShort(editingContact.contact_date)}
                        </span>
                        {editingContact.next_contact_date ? (
                          <span className="rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-900">
                            Hẹn lại: {formatDateShort(editingContact.next_contact_date)}
                          </span>
                        ) : null}
                      </div>

                      <div className="mt-3.5 rounded-xl border border-white/80 bg-white/90 p-3.5">
                        <p className="mb-1.5 text-[10px] font-black uppercase tracking-wider text-gray-400">
                          Nội dung đã ghi
                        </p>
                        <p className="text-[13px] leading-relaxed text-gray-800">
                          {editingContact.content || "Không có nội dung."}
                        </p>
                        {editingContact.result ? (
                          <p className="mt-2.5 border-t border-gray-100 pt-2.5 text-xs font-semibold text-emerald-700">
                            Kết quả: {editingContact.result}
                          </p>
                        ) : null}
                      </div>

                      <p className="mt-3 text-[11px] font-medium text-amber-800/90">
                        Đọc lại nội dung trên, rồi ghi nhận tiến độ mới ở phần dưới.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                      <Users className="h-3 w-3" /> Khách hàng
                    </h3>

                    <div>
                      <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                        Chọn khách hàng <span className="text-red-700">*</span>
                      </label>
                      <Select
                        options={customerOptions}
                        value={
                          customerOptions.find(
                            (o) => o.value === formData.customer_id
                          ) || null
                        }
                        onChange={(opt) => {
                          const selected = customers.find(
                            (c) => c.id === opt?.value
                          );
                          setFormData((prev) => ({
                            ...prev,
                            customer_id: opt?.value || "",
                            branch_id:
                              selected?.branch_id ||
                              fixedBranchId ||
                              prev.branch_id ||
                              "",
                          }));
                        }}
                        placeholder={
                          customersLoading
                            ? "Đang tải khách hàng..."
                            : "Chọn khách hàng..."
                        }
                        styles={customerSelectStyles}
                        isClearable
                        isLoading={customersLoading}
                        isDisabled={customersLoading}
                        noOptionsMessage={() =>
                          customersLoading
                            ? "Đang tải..."
                            : "Không có khách hàng trong phạm vi của bạn"
                        }
                        formatOptionLabel={formatCustomerOptionLabel}
                        filterOption={filterCustomerOption}
                        {...modalSelectPortalProps}
                      />
                    </div>
                  </div>
                )}

                {/* Tiến độ mới / ghi nhận mới */}
                <div className="space-y-3">
                  {isProgressMode ? (
                    <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                      <Phone className="h-3 w-3" /> Tiến độ mới
                    </h3>
                  ) : (
                    <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                      <Phone className="h-3 w-3" /> Thông tin liên hệ
                    </h3>
                  )}

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
                    <div>
                      <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                        Hình thức <span className="text-red-700">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2.5 md:hidden">
                        {methodOptions.map((opt) => {
                          const active = formData.method === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => handleChange("method", opt.value)}
                              className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                                active
                                  ? "border-red-700 bg-red-50 text-red-700"
                                  : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      <div className="hidden md:block">
                        <Select
                          options={methodOptions}
                          value={
                            methodOptions.find(
                              (opt) => opt.value === formData.method
                            ) || null
                          }
                          onChange={(opt) =>
                            handleChange("method", opt?.value || "")
                          }
                          placeholder="Chọn hình thức..."
                          styles={modalSelectStyles}
                          isClearable
                          menuPortalTarget={document.body}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                        Ngày liên hệ <span className="text-red-700">*</span>
                      </label>
                      <div className="relative group">
                        <Calendar className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors group-focus-within:text-red-700" />
                        <input
                          type="date"
                          value={formData.contact_date || ""}
                          onChange={(e) =>
                            handleChange("contact_date", e.target.value)
                          }
                          className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-12 pr-4 text-sm font-bold text-gray-700 transition-all focus:border-red-700 focus:outline-none focus:ring-4 focus:ring-red-700/5"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Nội dung & Kết quả */}
                <div className="space-y-4">
                  <h3 className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-gray-400">
                    <MessageSquare className="h-3 w-3" /> Chi tiết trao đổi
                  </h3>

                  <div>
                    <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Nội dung trao đổi <span className="text-red-700">*</span>
                    </label>
                    <textarea
                      rows={3}
                      value={formData.content || ""}
                      onChange={(e) => handleChange("content", e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700 transition-all focus:border-red-700 focus:outline-none focus:ring-4 focus:ring-red-700/5"
                      placeholder="Ghi chú chi tiết nội dung cuộc gọi, email hoặc gặp mặt..."
                    />
                  </div>

                  <div>
                    <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                      Kết quả cuối cùng <span className="text-red-700">*</span>
                    </label>
                    <div className="mb-2.5 flex flex-wrap gap-2">
                      {resultQuickOptions.map((opt) => {
                        const active = formData.result === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleChange("result", opt)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                              active
                                ? "border-red-700 bg-red-50 text-red-700"
                                : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    <input
                      type="text"
                      value={formData.result || ""}
                      onChange={(e) => handleChange("result", e.target.value)}
                      placeholder="Hoặc nhập kết quả khác..."
                      className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm font-bold text-gray-700 transition-all focus:border-red-700 focus:outline-none focus:ring-4 focus:ring-red-700/5"
                    />
                  </div>
                </div>

                {/* Section 3: Nhắc nhở */}
                <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-4 text-white sm:p-5">
                  <div className="relative z-10 flex flex-col justify-between gap-4 lg:flex-row lg:items-center lg:gap-6">
                    <div className="min-w-0">
                      <h3 className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.18em] text-red-500">
                        <Clock className="h-4 w-4" /> Tự động nhắc nhở
                      </h3>
                      <p className="text-sm font-medium leading-relaxed text-gray-400">
                        Hệ thống sẽ tạo lịch nhắc tự động cho bạn vào ngày này.
                      </p>
                    </div>

                    <div className="w-full lg:w-64">
                      <label className="mb-2 ml-1 block text-[10px] font-black uppercase tracking-widest text-gray-400">
                        Ngày hẹn liên hệ lại
                      </label>
                      <div className="relative group">
                        <Calendar className="pointer-events-none absolute left-4 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white/90" />
                        <input
                          type="date"
                          value={formData.next_contact_date || ""}
                          onChange={(e) =>
                            handleChange("next_contact_date", e.target.value)
                          }
                          className="contact-reminder-date w-full rounded-xl border border-slate-700 bg-slate-800 py-2.5 pl-12 pr-11 text-sm font-black text-white transition-all focus:border-red-500 focus:outline-none"
                        />
                        <Calendar className="pointer-events-none absolute right-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="pointer-events-none absolute right-0 top-0 p-4 opacity-5">
                    <Clock className="h-20 w-20 sm:h-24 sm:w-24" />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div
                className="flex shrink-0 items-center gap-3 border-t border-gray-100 bg-gray-50/80 px-5 pt-4 sm:justify-end sm:px-6 sm:pt-5"
                style={{
                  paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  className="min-h-[48px] flex-1 rounded-2xl border border-gray-200 bg-white px-5 py-3 text-sm font-bold text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 active:scale-[0.98] sm:flex-none sm:min-w-[120px] sm:rounded-full sm:px-8"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex min-h-[48px] flex-[1.35] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-red-700 to-red-800 px-5 py-3 text-sm font-bold text-white shadow-md shadow-red-100 transition hover:shadow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:min-w-[160px] sm:rounded-full sm:px-8"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  {loading
                    ? "Đang xử lý..."
                    : mode === "progress"
                      ? "Lưu tiến độ"
                      : "Ghi nhận"}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ContactHistoryCreateModal;
